import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import type { APIError, Assistant, Conversation, Message } from '../../../api/types';
import { Chat } from '../../Chat';
import { AssistantListPanel } from '../../panels/AssistantListPanel';
import { ChatHistoryPanel } from '../../panels/ChatHistoryPanel';
import { QuickPromptsPanel } from '../../panels/QuickPromptsPanel';
import { ErrorDisplay, FloatingButton, Icon } from '../../shared';
import type { ChatView } from '../../core/ChatNavigation';
import { FloatingWindow } from './FloatingWindow';
import { buildFullExperienceUrl } from '../../../config/fullexperience';
import { getResponsiveFloatingWindowSize, getResponsiveFloatingWindowPosition } from '../../../util/cssVariables';
import styles from './FloatingLayout.module.css';

export interface FloatingLayoutProps {
  messages: Message[];
  streamingMessage: string;
  isSending: boolean;
  isLoading: boolean;
  error?: APIError | null;
  onSendMessage: (message: string) => void;
  onClearError?: () => void;

  assistants?: Assistant[];
  currentAssistantId?: string;
  onSelectAssistant?: (assistantId: string) => void;
  isLoadingAssistants?: boolean;

  conversations?: Conversation[];
  currentConversationId?: string;
  onSelectConversation?: (conversationId: string) => void;
  onNewChat?: () => void;
  isLoadingConversations?: boolean;

  title?: string;
  onExpand?: () => void;
  onRefresh?: () => void;
  showExpand?: boolean;
  showRefresh?: boolean;
  showAssistantsView?: boolean;
  showHistoryView?: boolean;
  showUsageGuidelines?: boolean;
  footerText?: string;
  showClearHistory?: boolean;
  onClearHistory?: () => void;

  defaultSize?: { width: number; height: number };
  enableDrag?: boolean;
  enableResize?: boolean;
  constrainToViewport?: boolean;

  onOpenChange?: (isOpen: boolean) => void;
  className?: string;

  defaultPosition?: { x: number; y: number };
  onSizeChange?: (size: { width: number; height: number }) => void;
  onPositionChange?: (position: { x: number; y: number }) => void;
  activeSidebarView?: ChatView;
  onSidebarViewChange?: (view: ChatView) => void;
  
  isOpen?: boolean;
  onIsOpenChange?: (isOpen: boolean) => void;

  navigationLayout?: 'sidebar' | 'horizontal';
  onPin?: () => void;
}

export function FloatingLayout({
  messages,
  streamingMessage,
  isSending,
  isLoading,
  error,
  onSendMessage,
  onClearError,

  assistants = [],
  currentAssistantId,
  onSelectAssistant,
  isLoadingAssistants = false,

  conversations = [],
  currentConversationId,
  onSelectConversation,
  onNewChat,
  isLoadingConversations = false,

  title,
  // onExpand,
  onRefresh,
  // showExpand = false,
  showRefresh = false,
  showAssistantsView = true,
  showHistoryView = true,
  showUsageGuidelines,
  footerText,
  showClearHistory = false,
  onClearHistory,

  defaultSize = { width: 400, height: 600 },
  enableDrag = true,
  enableResize = true,

  onOpenChange,
  className = '',

  defaultPosition,
  onSizeChange,
  onPositionChange,
  activeSidebarView = 'chat',
  onSidebarViewChange,
  isOpen: isOpenProp,
  onIsOpenChange,
  navigationLayout = 'sidebar',
  onPin,
}: FloatingLayoutProps) {
  const [isOpen, setIsOpen] = useState(isOpenProp ?? false);

  useEffect(() => {
    if (isOpenProp !== undefined) {
      setIsOpen(isOpenProp);
    }
  }, [isOpenProp]);

  // Get responsive default position (bottom-right)
  const responsiveDefaultPosition = useMemo(() => getResponsiveFloatingWindowPosition(), []);
  const [position, setPosition] = useState(defaultPosition ?? responsiveDefaultPosition);
  
  // Use provided defaultSize, or fallback to CSS variables if not provided
  const responsiveDefaultSize = useMemo(() => getResponsiveFloatingWindowSize(), []);
  const [size, setSize] = useState(defaultSize ?? responsiveDefaultSize);
  const [currentView, setCurrentView] = useState<ChatView>(activeSidebarView);

  useEffect(() => {
    setCurrentView(activeSidebarView);
  }, [activeSidebarView]);

  useEffect(() => {
    if (defaultSize) {
      setSize(defaultSize);
    }
  }, [defaultSize]);

  useEffect(() => {
    if (defaultPosition) {
      setPosition(defaultPosition);
    }
  }, [defaultPosition]);

  const handleSizeChange = useCallback((newSize: { width: number; height: number }) => {
    setSize(newSize);
    onSizeChange?.(newSize);
  }, [onSizeChange]);

  const handlePositionChange = useCallback((newPosition: { x: number; y: number }) => {
    setPosition(newPosition);
    onPositionChange?.(newPosition);
  }, [onPositionChange]);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, streamingMessage, isOpen]);

  const handleToggle = useCallback(() => {
    const newOpen = !isOpen;
    setIsOpen(newOpen);
    onIsOpenChange?.(newOpen);
    onOpenChange?.(newOpen);
  }, [isOpen, onOpenChange, onIsOpenChange]);

  const handleSend = useCallback(
    (message: string) => {
      onSendMessage(message);
      if (currentView !== 'chat') {
        setCurrentView('chat');
      }
    },
    [onSendMessage, currentView]
  );

  const handleSelectPrompt = useCallback(
    (prompt: string) => {
      handleSend(prompt);
    },
    [handleSend]
  );

  const handleSelectAssistant = useCallback(
    (assistantId: string) => {
      if (onSelectAssistant) {
        onSelectAssistant(assistantId);
      }
      setCurrentView('chat');
    },
    [onSelectAssistant]
  );

  const handleSelectConversation = useCallback(
    (conversationId: string) => {
      if (onSelectConversation) {
        onSelectConversation(conversationId);
      }
      setCurrentView('chat');
    },
    [onSelectConversation]
  );

  const handleNewChat = useCallback(() => {
    if (onNewChat) {
      onNewChat();
    }
    setCurrentView('chat');
  }, [onNewChat]);

  const handleViewChange = useCallback(
    (view: ChatView) => {
      setCurrentView(view);
      onSidebarViewChange?.(view);
    },
    [onSidebarViewChange]
  );

  const getHeaderTitle = () => {
    if (title) return title;

    switch (currentView) {
      case 'assistants':
        return 'All Assistants';
      case 'history':
        return 'Chat History';
      case 'chat':
      default:
        return currentAssistantId
          ? assistants.find((a) => a.assistant_id === currentAssistantId)?.title || 'DevXGPT'
          : 'DevXGPT';
    }
  };

  const hasMessages = messages.length > 0 || streamingMessage;
  const currentAssistant = assistants.find((a) => a.assistant_id === currentAssistantId);
  const currentPrompts = currentAssistant?.prompts || [];
  const showQuickPrompts = currentView === 'chat' && !hasMessages && currentPrompts.length > 0;

  const latestThreadId = useMemo(() => {
    if (messages.length === 0) return undefined;
    const latestMessage = messages[messages.length - 1];
    return latestMessage.message_metadata?.thread_id;
  }, [messages]);

  const handleOpenFullExperience = useCallback(() => {
    const url = buildFullExperienceUrl({
      view: currentView,
      conversationId: currentConversationId,
      threadId: latestThreadId,
      assistantId: currentAssistantId,
    });
    
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [currentView, currentConversationId, latestThreadId, currentAssistantId]);

  const headerActions = (
    <>
      {showRefresh && onRefresh && (currentView === 'assistants' || currentView === 'history') && (
        <button
          className={styles.actionButton}
          onClick={onRefresh}
          aria-label="Refresh"
          title="Refresh"
        >
          <Icon name="refresh" size={16} />
        </button>
      )}
      {onPin && (
        <button
          className={styles.actionButton}
          onClick={onPin}
          aria-label="Pin to sidebar"
          title="Pin to sidebar"
        >
          <Icon name="pin" size={16} />
        </button>
      )}
      {/* {showExpand && onExpand && (
        <button
          className={styles.actionButton}
          onClick={onExpand}
          aria-label="Expand to max size"
          title="Expand to max size"
        >
          <Icon name="expand" size={16} />
        </button>
      )} */}
      {/* {showExpand && onExpand && ( */}
        <button
          className={styles.actionButton}
          onClick={handleOpenFullExperience}
          aria-label="Open full experience"
          title="Open full experience"
        >
          <Icon name="open-in-new" size={16} />
        </button>
      {/* )} */}
    </>
  );

  return (
    <div className={`${styles.container} ${className}`}>
      {!isOpen && (
        <FloatingButton
          onClick={handleToggle}
          icon="devxgpt-logo"
          ariaLabel="Open chat"
          position="bottom-right"
          iconOnly
        />
      )}

      {isOpen && (
        <FloatingWindow
          position={position}
          size={size}
          onPositionChange={handlePositionChange}
          onSizeChange={handleSizeChange}
          onClose={handleToggle}
          enableDrag={enableDrag}
          enableResize={enableResize}
          navigationLayout={navigationLayout}
        >
          {navigationLayout === 'sidebar' ? (
            <div className={styles.windowContent}>
              <Chat.Navigation
                currentView={currentView}
                onViewChange={handleViewChange}
                showAssistants={showAssistantsView}
                showHistory={showHistoryView}
                variant="compact"
              />

              <div className={styles.mainContent}>
                <Chat variant="floating" conversationId={currentConversationId}>
                  <Chat.Header
                    title={getHeaderTitle()}
                    variant="compact"
                    onClose={handleToggle}
                    actions={headerActions}
                  />

                  {error && <ErrorDisplay error={error} onDismiss={onClearError} />}

                  <div className={styles.content}>
                    {currentView === 'assistants' && (
                      <AssistantListPanel
                        assistants={assistants}
                        currentAssistantId={currentAssistantId}
                        onSelectAssistant={handleSelectAssistant}
                        isLoading={isLoadingAssistants}
                      />
                    )}

                    {currentView === 'history' && (
                      <ChatHistoryPanel
                        conversations={conversations}
                        currentConversationId={currentConversationId}
                        onSelectConversation={handleSelectConversation}
                        onNewChat={handleNewChat}
                        isLoading={isLoadingConversations}
                      />
                    )}

                    {currentView === 'chat' && (
                      <>
                        {showQuickPrompts ? (
                          <QuickPromptsPanel
                            prompts={currentPrompts}
                            onSelectPrompt={handleSelectPrompt}
                            isLoading={isLoading}
                            title={`${currentAssistant?.title || 'DevXGPT'}`}
                          />
                        ) : (
                          <div className={styles.messagesContainer}>
                            <Chat.Messages messages={messages} isLoading={isLoading} />
                            {isSending && !streamingMessage && <Chat.TypingIndicator />}
                            {streamingMessage && <Chat.StreamingMessage content={streamingMessage} />}
                            <div ref={messagesEndRef} />
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {currentView === 'chat' && (
                    <Chat.Input onSend={handleSend} disabled={isSending} isSending={isSending} />
                  )}

                  <Chat.Footer
                    showUsageGuidelines={showUsageGuidelines}
                    footerText={footerText}
                    showClearHistory={showClearHistory && currentView === 'history'}
                    onClearHistory={onClearHistory}
                    variant="compact"
                  />
                </Chat>
              </div>
            </div>
          ) : (
            <div className={styles.windowContentHorizontal}>
              <Chat variant="floating" conversationId={currentConversationId}>
                <div className={styles.headerWithLogo}>
                  <div className={styles.logoAndTitle}>
                    <Icon name="devxgpt-logo" size={32} aria-hidden={false} />
                    <h2 className={styles.headerTitle}>{getHeaderTitle()}</h2>
                  </div>
                  <div className={styles.headerActions}>
                    {headerActions}
                    <button
                      className={styles.actionButton}
                      onClick={handleToggle}
                      aria-label="Close"
                      title="Close"
                    >
                      <Icon name="close" size={16} />
                    </button>
                  </div>
                </div>

                <Chat.Navigation
                  currentView={currentView}
                  onViewChange={handleViewChange}
                  showAssistants={showAssistantsView}
                  showHistory={showHistoryView}
                  variant="floating"
                />

                {error && <ErrorDisplay error={error} onDismiss={onClearError} />}

                <div className={styles.content}>
                  {currentView === 'assistants' && (
                    <AssistantListPanel
                      assistants={assistants}
                      currentAssistantId={currentAssistantId}
                      onSelectAssistant={handleSelectAssistant}
                      isLoading={isLoadingAssistants}
                    />
                  )}

                  {currentView === 'history' && (
                    <ChatHistoryPanel
                      conversations={conversations}
                      currentConversationId={currentConversationId}
                      onSelectConversation={handleSelectConversation}
                      onNewChat={handleNewChat}
                      isLoading={isLoadingConversations}
                    />
                  )}

                  {currentView === 'chat' && (
                    <>
                      {showQuickPrompts ? (
                        <QuickPromptsPanel
                          prompts={currentPrompts}
                          onSelectPrompt={handleSelectPrompt}
                          isLoading={isLoading}
                          title={`${currentAssistant?.title || 'DevXGPT'}`}
                        />
                      ) : (
                        <div className={styles.messagesContainer}>
                          <Chat.Messages messages={messages} isLoading={isLoading} />
                          {isSending && !streamingMessage && <Chat.TypingIndicator />}
                          {streamingMessage && <Chat.StreamingMessage content={streamingMessage} />}
                          <div ref={messagesEndRef} />
                        </div>
                      )}
                    </>
                  )}
                </div>

                {currentView === 'chat' && (
                  <Chat.Input onSend={handleSend} disabled={isSending} isSending={isSending} />
                )}

                <Chat.Footer
                  showUsageGuidelines={showUsageGuidelines}
                  footerText={footerText}
                  showClearHistory={showClearHistory && currentView === 'history'}
                  onClearHistory={onClearHistory}
                  variant="compact"
                />
              </Chat>
            </div>
          )}
        </FloatingWindow>
      )}
    </div>
  );
}
